/**
 * AuthGate — boot-time auth boundary for the KDS operator SPA.
 *
 * Industry-standard pattern for BFF-cookie-authenticated SPAs (Auth0
 * SPA SDK, react-oidc-context, MSAL): probe a session-protected endpoint
 * once on mount, branch on the outcome, and never render protected UI
 * until the probe resolves. This is the *single* source of truth for
 * "do I need to sign in?" — no per-page or per-request handler races
 * with it.
 *
 * Why the SPA can't rely on a BFF redirect (the way PF + HS do) — the
 * KDS operator SPA is served from admin.tovriktech.dev (proxied via
 * admin-router from kds-spa.pages.dev), but the BFF (kds-api) is on
 * kds-api.tovriktech.dev. A browser hitting admin.tovriktech.dev gets
 * the SPA bundle directly without traversing the BFF, so the BFF never
 * gets a chance to 302 to OIDC. The probe is JS-side instead.
 *
 * Behavior:
 *   • Mount → call api.listDevices(merchantId) as the auth probe (the
 *     same endpoint the device list page uses — saves adding a /me
 *     route to kds-api just for this).
 *   • 2xx → render children (the route).
 *   • 401 → `window.location.assign(${KDS_API_BASE}/auth/sign-in?return=…)`.
 *     kds-api 302s to id.tovriktech.dev/oauth/authorize; IDS silent-grants
 *     against the apex-scoped msi_session; callback mints kds_session
 *     (Domain=.tovriktech.dev per worker config); user lands back here
 *     with a valid session, and the probe re-runs on the fresh mount.
 *   • 403 with `error: 'forbidden_wrong_merchant'` → same redirect as 401.
 *     This is the "user signed in for merchant A, now opening /m/B"
 *     case. The kds_session is valid but its merchant_id claim doesn't
 *     match the URL. Re-running the OIDC dance against IDS mints a
 *     fresh kds_session bound to merchant B. Without this, the SPA
 *     used to render the page and surface the red 403 toast — a UX
 *     dead-end. (2026-05-23 fix; merchantSession middleware tagged
 *     the error code to make this branch possible.)
 *   • Other 403 / 5xx / network outage → render children anyway so
 *     they can surface their own error UI. The probe is for auth
 *     gating, not transient health checks.
 *
 * Standalone dev mode (npm run dev, mer_demo merchantId) is handled by
 * the caller — App.tsx renders the SPA without AuthGate when the path
 * doesn't match /m/{mid}/...
 */

import { useEffect, useState, type ReactNode } from 'react';
import { matchMount } from '@tovriktech/shell-mounts';
import { api, ApiError, KDS_API_BASE } from '../lib/api';

/**
 * Build the sign-in redirect target for a re-auth.
 *
 * WS-PHASE2-SHELL Stage 3 (DL-10) + staff-support-mount (2026-07-09). Under
 * EITHER same-origin shell scheme for product 'kds' AUTH is owned by the apex
 * shell, not by kds-api:
 *   • STATIC operator mount (`app.tovrik.dev/kds`)
 *   • STAFF support mount   (`/m/{mid}/kds` — a platform support user acting
 *     ON a merchant; their session lives on the apex shell, NOT on kds-api)
 * so both DEFER to the shell's same-origin `/login`, letting it run the OIDC
 * dance + mint the apex session cookie the SPA's same-origin XHR (DL-2) rides
 * on. (Adding the STAFF mount here is the support-deep-link fix: it previously
 * fell through to the kds-api sign-in below, bounced the staffer off the
 * cross-origin apex session, and looped on 403 forbidden_wrong_merchant.)
 *
 * SHELL branch FIRST. At the STANDALONE kds.tovrik.dev surface (DL-3, root-
 * relative paths — matchMount returns null) this is BYTE-FOR-BYTE UNCHANGED:
 * bounce to `${KDS_API_BASE}/auth/sign-in?return=<href>`, where KDS_API_BASE
 * is the build-time absolute kds-api URL.
 */
function signInRedirectTarget(): string {
  const m = matchMount(window.location.pathname);
  if (m && m.product === 'kds') {
    // STATIC or STAFF shell mount → defer to the shell's login. No ?return:
    // the shell restores the deep link from its own session/return handling.
    return '/login';
  }
  const returnUrl = window.location.href;
  return `${KDS_API_BASE}/auth/sign-in?return=${encodeURIComponent(returnUrl)}`;
}

export interface AuthGateProps {
  merchantId: string;
  children: ReactNode;
}

type ProbeState = { kind: 'probing' } | { kind: 'ok' } | { kind: 'redirecting' };

export function AuthGate({ merchantId, children }: AuthGateProps): ReactNode {
  const [state, setState] = useState<ProbeState>({ kind: 'probing' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await api.listDevices(merchantId);
        if (!cancelled) setState({ kind: 'ok' });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && shouldReauth(e)) {
          setState({ kind: 'redirecting' });
          // Shell-aware (DL-10): defer to the shell's /login under the
          // one-origin mount, else the standalone kds-api sign-in.
          const signIn = signInRedirectTarget();
          // location.assign so the back button doesn't trap the user
          // in a sign-in loop after they cancel at the IDS magic-link form.
          window.location.assign(signIn);
          return;
        }
        setState({ kind: 'ok' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [merchantId]);

  if (state.kind === 'probing' || state.kind === 'redirecting') {
    return <ProbingShell label={state.kind === 'redirecting' ? 'Signing you in…' : 'Loading…'} />;
  }
  return <>{children}</>;
}

/**
 * Decide whether an ApiError should trigger the OIDC re-auth dance
 * vs. fall through to "render children, let the page surface its
 * error UI."
 *
 *   401                         — always; no/expired kds_session
 *   403 forbidden_wrong_merchant — kds_session valid but bound to
 *                                  a different merchant; re-auth
 *                                  mints one for the URL merchant
 *
 * Other 403 cases (user genuinely lacks access to this merchant, or
 * the merchant doesn't have KDS subscribed) should NOT re-auth —
 * re-auth would just produce the same 403 in an infinite loop.
 */
function shouldReauth(e: ApiError): boolean {
  if (e.status === 401) return true;
  if (e.status === 403) {
    const body = e.body as { error?: string } | null | undefined;
    return body?.error === 'forbidden_wrong_merchant';
  }
  return false;
}

function ProbingShell({ label }: { label: string }): ReactNode {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[50vh] items-center justify-center"
    >
      <div className="text-sm text-tvk-fg-muted">{label}</div>
    </div>
  );
}
